import cn from "classnames";
import { useEffect, useState, useCallback, useRef } from "react";

// types
import { Generic } from "types/filters-store.types";
import { ChangesProps } from "../ServiceSection";

// store
import { useProfileEditStore } from "store/useProfileEditStore";

// fetch
import { updateExtendedProfileData } from "data/fetch";

// components
import {
  Button,
  MultiSelectCheckbox,
} from "components/LibraryComponents/Components";
import { Saved } from "components/EditBusinessProfileSections/shared/Saved/Saved";
import { ResponsiveStack } from "components/ResponsiveStack/ResponsiveStack";

// hooks 
import { useMeiliTaskToast } from "hooks/useMeiliTaskToast";
import { useSaveConfirmation } from "hooks/useSaveConfirmation";

// utils
import { saveErrorToast } from "utils/edit-profile";

// constants
import { QASList } from "constants/filters";

// styles
import styles from "../ServiceSection.module.scss";

type Props = {
  setUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
  saveConfirmation: ReturnType<typeof useSaveConfirmation>;
  section: string;
};

export default function QualityAssuranceSection({ setUnsavedChanges, saveConfirmation, section }: Props) {
  const { extendedProfile, setExtendedProfile } = useProfileEditStore(
    (state) => ({
      extendedProfile: state.extendedProfile,
      setExtendedProfile: state.setExtendedProfile,
    })
  );

  const [selectedItems, setSelectedItems] = useState<Generic[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const initialSelectedItems = useRef<Generic[]>([]); // Store initial quality_assurance_standards for change detection
  const isInitialized = useRef(false); // Track if selectedItems has been initialized

  const { 
    lastSavedSection,
    showConfirmation,
    clearConfirmation
  } = saveConfirmation;

  const addTask = useMeiliTaskToast();

  // Parse quality_assurance_standards string to Generic[]
  const parseQualityAssuranceStandards = (
    standards: string | null
  ): Generic[] => {
    if (!standards) return [];
    return standards
      .split(";")
      .map((standard) => standard.trim())
      .filter((standard) => standard)
      .map((standard) => ({
        label: standard,
        value: standard,
      }));
  };

  // Convert selectedItems to quality_assurance_standards string
  const formatQualityAssuranceStandards = (items: Generic[]): string => {
    return items.map((item) => item.value).join("; ");
  };

  // Check if selectedItems has changed
  const hasFieldChanged = useCallback(() =>
    formatQualityAssuranceStandards(selectedItems) !==
    formatQualityAssuranceStandards(initialSelectedItems.current), [selectedItems, initialSelectedItems])

  // Check if selectedItems is empty
  const isFormEmpty = selectedItems.length === 0;

  // Update unsaved changes state
  useEffect(() => {
    setUnsavedChanges(hasFieldChanged());
    clearConfirmation();
    return () => {
      setUnsavedChanges(false);
    };
  }, [selectedItems, setUnsavedChanges]);

  // Initialize selectedItems from extendedProfile
  useEffect(() => {
    if (extendedProfile && !isInitialized.current && isFormEmpty) {
      const initial = parseQualityAssuranceStandards(
        extendedProfile.quality_assurance_standards
      );
      setSelectedItems(initial);
      initialSelectedItems.current = initial;
      isInitialized.current = true;
    }
  }, [extendedProfile, isFormEmpty]);

  const handleChange = (selectedItems: Generic[]) => {
    setSelectedItems(selectedItems);
  };

  const handleSaveField = async () => {
    if (!hasFieldChanged() || isSaving) return;

    setIsSaving(true);
    try {
      const qualityAssuranceStandards =
        formatQualityAssuranceStandards(selectedItems);

       const response = await updateExtendedProfileData(
        extendedProfile.uei,
        JSON.stringify({
          quality_assurance_standards: qualityAssuranceStandards,
        })
      );

      // Simulate network delay
      // await new Promise((resolve) => setTimeout(resolve, 1000));

      setExtendedProfile({
        ...extendedProfile,
        quality_assurance_standards: qualityAssuranceStandards,
      });

      initialSelectedItems.current = [...selectedItems]; // Update initial values after save

      const responseData = await response.json();
      addTask({
        taskUid: responseData?.taskUid,
        label: section,
        status: responseData?.status,
      });
      showConfirmation(section);
    } catch (error: any) {
      console.error(`Error saving quality assurance:`, error);
      saveErrorToast();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={cn(styles["section-content"], styles["space-y-6"])}>
      <div>
        <h3 className={styles["title"]}>Quality Assurance Standards</h3>
        <p className={styles["desc"]}>
          Select any quality assurance standards your small business uses.
        </p>
      </div>

      <MultiSelectCheckbox
        items={QASList}
        checkedItems={selectedItems}
        handleChange={handleChange}
        hideOnly
      />

      <ResponsiveStack>
        <Button
          type="button"
          loading={isSaving}
          disabled={!hasFieldChanged() || isSaving}
          onClick={handleSaveField}
        >
          {isSaving ? "Saving..." : "Save changes"}
        </Button>
        {lastSavedSection === section && <Saved />}
      </ResponsiveStack>
    </div>
  );
}
